import * as React from 'react';
import { TextEdit } from '../../../text-edit';
import { PageProps } from './page.types';
import { usePageController } from './page.controller';
import { TextField } from '../../../text-field';
import { I18n } from 'react-redux-i18n';
import { Fields } from '../../../fields';
import { Flex } from '@common/components/Flex';
import { Title } from '../../../title';
import { Button } from '@common/components/Button';
import { BackBtn } from '../../../back-btn';
import { FieldSelector } from '@common/components/FieldSelector';
import { Field } from '../field';
import { Subtitle } from '../../../subtitle';
import { RadioGroup, RadioGroupValue } from '../../../radio-group';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { SkipLogic } from '@common/components/skip-logic';
import { FieldProps } from '../field/field.types';
import { Loading } from '@common/components/loading';
import { SkipLogicPredefinedControlType, SkipLogicProps } from '@common/components/skip-logic/skip-logic.types';
import { FieldSelectorGetFieldParams } from '@common/components/FieldSelector/field-selector.types';
import { NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES } from '@modules/fhq-design-tool/constants/page';

import './page.styles.scss';

export const Page: React.FC<PageProps> = (props) => {
  const { cachedData, showConfirmMessage, onGoBack, getFields, getFolders, scrollParentToTop, errors } = props;

  const {
    loadingPage,
    processedFields,
    familyPagesSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    handleFieldChange,
    goBackFromFieldEdit,
    editingField,
    handleFieldAdd,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
    handlePageNameChange,
    handlePageDescriptionChange,
    fieldHasError,
  } = usePageController(props);

  if (editingField) {
    return (
      <Field
        errors={errors?.fields ? errors?.fields[editingField.progenyId] : undefined}
        scrollParentToTop={scrollParentToTop}
        data={editingField}
        onGoBack={goBackFromFieldEdit}
        onChange={handleFieldChange as FieldProps['onChange']}
        showConfirmMessage={showConfirmMessage}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
      />
    );
  }

  if (loadingPage || !cachedData) {
    return (
      <div className="FhqDesignToolQuestionnaireDesignerPage">
        <Loading size={20} margin="20px" />
      </div>
    );
  }

  return (
    <div className="FhqDesignToolQuestionnaireDesignerPage">
      <BackBtn className="FhqDesignToolQuestionnaireDesignerPage__back-btn" onClick={onGoBack} />
      <TextEdit 
        required
        errors={errors?.properties?.title}
        metadata={{
          description: 'page-name',
        }}
        value={cachedData.title}
        onChange={handlePageNameChange}
      />
      <TextField 
        metadata={{
          description: 'page-description',
        }}
        type="textarea"
        classes={{
          label: 'FhqDesignToolQuestionnaireDesignerPage__description-label',
          labelInputWrapper: 'FhqDesignToolQuestionnaireDesignerPage__description-input-label-wrapper'
        }}
        label={I18n.t('Page Description')}
        value={cachedData.description ?? ''}
        onChange={handlePageDescriptionChange}
      />
      <Title text={I18n.t('Skip Logic')} />
      <Subtitle
        text={I18n.t('skipLogicDescriptionPage')}
      />
      <RadioGroup
        margin="12px 0 12px 0"
        value={JSON.stringify(cachedData.skipLogicEnabled ?? false)}
        options={[
          { id: "false", name: I18n.t("Show Always") },
          { id: "true", name: I18n.t("Show only if condition below is true"), },
        ]}
        onChange={handleUseSkipLogicChange as (v: RadioGroupValue) => void}
        metadata={{
          description: "page-use-skip-logic",
        }}
      />
      {cachedData.skipLogicEnabled && (
        <SkipLogic
          moduleName={MODULE_NAME}
          className="FhqDesignToolQuestionnaireDesignerPage__skip-logic"
          conditions={familyPagesSkipLogicConditions}
          onRemove={handleSkipLogicChange}
          onChange={handleSkipLogicChange}
          onAdd={handleSkipLogicConditionAdd as SkipLogicProps['onAdd']}
          getFolders={getFolders}
          getFields={getFields}
          controls={[
            { type: SkipLogicPredefinedControlType.moveUp, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.moveDown, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.add },
          ]} 
        />
      )}
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerPage__add-field-header">
        <Title text={I18n.t('Fields')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesignerPage__add-field-btn" onClick={openFieldChooser}>
          +
        </Button>
      </Flex>
      <Fields 
        hasError={fieldHasError}
        data={processedFields}
        canShowHideField={canShowHideField}
        onOrderChange={handleFieldsOrderChange}
        showDeleteConfirmMessage={showConfirmMessage}
        onEdit={navigateOnFieldEdit}
        onDelete={handleFieldDelete}
      />
      {fieldChooserOpened && (
        <FieldSelector
          withSearch
          getFolders={getFolders}
          getFields={({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) => 
            getFields({ searchQuery: searchKeyword, ...restParams, includeTables: true })
              .then((fields) => 
                fields.filter((x) => !NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES.includes(x.fieldTypeId))
              )
          }
          onChange={handleFieldAdd}
          close={closeFieldChooser}
          closeOnChange={false}
          multiple
        />
      )}
    </div>
  );
};